/**
 * Philippine-timezone date helpers.
 *
 * HiponHero operates exclusively on Philippine local dates (Asia/Manila, UTC+8).
 * The backend expects `YYYY-MM-DD` strings interpreted as PH-local days; if the
 * client formats `new Date()` in the user's browser timezone, a supervisor in
 * UTC-5 reviewing data at 11 pm local time would generate "tomorrow" in PH
 * terms, and the row lands under the wrong reading_date.
 *
 * Always route date-string formation through the helpers in this file — never
 * `format(new Date(), 'yyyy-MM-dd')` directly. Issue #88.
 */

const PH_TZ = 'Asia/Manila';

/**
 * Format an arbitrary Date (or the current instant, if omitted) as a PH-local
 * `YYYY-MM-DD` string. Uses Intl's `en-CA` locale, which conveniently emits
 * ISO-8601 date format.
 */
export function formatPhDate(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PH_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Shorthand for today's date in PH local terms. */
export function todayInPH(): string {
  return formatPhDate();
}

/**
 * Parse a `YYYY-MM-DD` string as a PH-local midnight Date object.
 * Useful when you need to feed a Date to components (like date pickers) that
 * expect one, but want it anchored to PH local calendar.
 *
 * NOTE: the returned Date is an absolute instant (`T00:00:00+08:00`), so
 * `.toISOString()` and `.toLocaleString()` may show other timezones. Only use
 * the return value for PH-aware pickers — for display, use formatPhDate() on
 * the way back out.
 */
export function parsePhDate(isoDate: string): Date {
  // `+08:00` is the unchanging PH offset (no DST).
  return new Date(`${isoDate}T00:00:00+08:00`);
}

/**
 * Shift a PH-local `YYYY-MM-DD` by N days, returning another PH-local string.
 * N can be negative for subtraction.
 */
export function addPhDays(isoDate: string, days: number): string {
  const d = parsePhDate(isoDate);
  d.setUTCDate(d.getUTCDate() + days);
  return formatPhDate(d);
}

/**
 * Locale-pinned date formatter (issue #125). Many routes used
 * `toLocaleDateString()` without an explicit locale/timeZone, so browsers
 * in non-PH timezones rendered stocking dates / feeding dates in the wrong
 * calendar day. Use this everywhere a Date or ISO-date string goes to the
 * screen as a human-readable date.
 */
export function displayPhDate(
  value: Date | string,
  opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
): string {
  const d = typeof value === 'string' ? parsePhDate(value) : value;
  return new Intl.DateTimeFormat('en-PH', { timeZone: PH_TZ, ...opts }).format(d);
}

export function displayPhTime(
  value: Date | string,
  opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }
): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-PH', { timeZone: PH_TZ, ...opts }).format(d);
}

/** Minutes since PH-local midnight for an instant (default: now). */
export function phMinutesOfDay(date: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PH_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get('hour') * 60 + get('minute');
}

/** A `datetime-local` value (`YYYY-MM-DDTHH:mm`) for an instant, in PH wall-clock time. */
export function phDateTimeLocal(date: Date = new Date()): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: PH_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .map((x) => [x.type, x.value])
  );
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** The inverse: a PH wall-clock `datetime-local` value as an ISO instant with the PH offset. */
export function phLocalToIso(local: string): string {
  return `${local}:00+08:00`;
}
